import { useState } from "react";
import { Calendar, Plus, Tv } from "lucide-react";

export const PlayoffBracketPreview = ({ playoff, teams, onEditMatchup, onQuickAssign }) => {
  const [activeSlot, setActiveSlot] = useState(null);
  
  const getTeamName = (teamId) => {
    if (!teamId) return "TBD";
    const team = teams.find(t => t.id === teamId);
    return team?.abbreviation || team?.name?.substring(0, 3) || "TBD";
  };

  const getTeamLogo = (teamId) => {
    if (!teamId) return null;
    const team = teams.find(t => t.id === teamId);
    return team?.logo_url;
  };

  const getAssignedTeamIds = () => {
    const assigned = new Set();
    playoff.matchups?.forEach(m => {
      if (m.team1_id) assigned.add(m.team1_id);
      if (m.team2_id) assigned.add(m.team2_id);
    });
    return assigned;
  };

  const handleQuickAssign = async (matchupId, slot, teamId) => {
    setActiveSlot(null);
    if (onQuickAssign) {
      await onQuickAssign(matchupId, slot, teamId);
    }
  };

  const isDoubleSided = playoff.bracket_type === "double_sided";
  // Calculate bracket size from team_count if bracket_size not directly set
  const teamCount = playoff.team_count || playoff.bracket_size || 8;
  const calculatedBracketSize = Math.pow(2, Math.ceil(Math.log2(teamCount)));
  const numRounds = Math.log2(calculatedBracketSize);

  const renderTeamSlot = (matchup, slot, side) => {
    const teamId = slot === 'team1' ? matchup.team1_id : matchup.team2_id;
    const seed = slot === 'team1' ? matchup.team1_seed : matchup.team2_seed;
    const score = slot === 'team1' ? matchup.team1_score : matchup.team2_score;
    const isWinner = matchup.winner_id && matchup.winner_id === teamId;
    const isEmpty = !teamId;
    const isActive = activeSlot?.matchupId === matchup.id && activeSlot?.slot === slot;
    const assignedTeamIds = getAssignedTeamIds();
    const availableTeams = teams.filter(t => !assignedTeamIds.has(t.id) || t.id === teamId);
    const logo = getTeamLogo(teamId);

    if (isActive) {
      return (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <select
            autoFocus
            className="w-full px-1.5 py-1 bg-zinc-800 border border-orange-500 rounded text-[11px] text-white focus:outline-none"
            value={teamId || ""}
            onChange={(e) => handleQuickAssign(matchup.id, slot, e.target.value || null)}
            onBlur={() => setActiveSlot(null)}
            data-testid={`quick-assign-${matchup.id}-${slot}`}
          >
            <option value="">-- Select Team --</option>
            {availableTeams.map(team => (
              <option key={team.id} value={team.id}>
                {team.abbreviation || team.name?.substring(0, 10)} {team.name?.length > 10 ? '' : `- ${team.name}`}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div 
        className={`flex justify-between p-1.5 ${isWinner ? "text-green-400 bg-green-500/10" : isEmpty ? "text-orange-400 bg-orange-500/5" : "text-zinc-400"} ${
          isEmpty ? "cursor-pointer hover:bg-orange-500/20" : ""
        } ${slot === 'team2' ? "border-t border-zinc-800" : ""}`}
        onClick={(e) => {
          if (isEmpty && matchup.round === 1) {
            e.stopPropagation();
            setActiveSlot({ matchupId: matchup.id, slot });
          }
        }}
        title={isEmpty && matchup.round === 1 ? "Click to assign team" : isEmpty ? "Winner advances from previous round" : ""}
      >
        <span className="truncate flex items-center gap-1">
          {side === "championship" && (
            <span className={`w-1.5 h-1.5 rounded-full ${slot === 'team1' ? 'bg-blue-500' : 'bg-red-500'} flex-shrink-0`}></span>
          )}
          {logo && <img src={logo} alt="" className="w-3 h-3 object-contain" />}
          {seed ? `${seed}.` : ""}{isEmpty ? (matchup.round === 1 ? <span className="flex items-center gap-0.5"><Plus className="w-2.5 h-2.5" />Add</span> : "TBD") : getTeamName(teamId)}
        </span>
        <span className="ml-1 font-mono">{score ?? "-"}</span>
      </div>
    );
  };

  const renderMatchup = (matchup, side = null, verticalOffset = 0) => (
    <div 
      key={matchup.id} 
      className={`bg-zinc-900 rounded text-xs cursor-pointer hover:ring-1 hover:ring-orange-500 transition-all ${
        side === "A" ? "border-l-2 border-blue-500" :
        side === "B" ? "border-r-2 border-red-500" :
        side === "championship" ? "border-2 border-orange-500" :
        "border border-zinc-700"
      }`}
      style={{ marginTop: verticalOffset > 0 ? `${verticalOffset}px` : undefined }}
      onClick={() => onEditMatchup && onEditMatchup(matchup)}
      title="Click to edit game details"
    >
      {(matchup.game_date || matchup.game_time) && (
        <div className="px-1.5 py-0.5 bg-zinc-800 text-zinc-500 text-[10px] border-b border-zinc-700 flex items-center gap-1">
          <Calendar className="w-2.5 h-2.5" />
          {matchup.game_date && new Date(matchup.game_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {matchup.game_time && ` ${matchup.game_time}`}
        </div>
      )}
      {renderTeamSlot(matchup, 'team1', side)}
      {renderTeamSlot(matchup, 'team2', side)}
      {(matchup.tv_network || matchup.youtube_url) && (
        <div className="px-1.5 py-0.5 bg-orange-500/10 text-orange-400 text-[10px] border-t border-zinc-700 flex items-center gap-1">
          <Tv className="w-2.5 h-2.5" />
          {matchup.tv_network || "Stream"}
        </div>
      )}
      {side === "championship" && matchup.winner_id && (
        <div className="p-1.5 border-t border-orange-500/50 bg-orange-500/10 text-center text-orange-400 font-bold">
          🏆 {getTeamName(matchup.winner_id)}
        </div>
      )}
    </div>
  );

  if (isDoubleSided) {
    const sideAMatchups = playoff.matchups?.filter(m => m.side === "A") || [];
    const sideBMatchups = playoff.matchups?.filter(m => m.side === "B") || [];
    const championship = playoff.matchups?.find(m => m.side === "championship");

    const sideARounds = [];
    const sideBRounds = [];
    for (let r = 1; r <= numRounds; r++) {
      sideARounds.push(sideAMatchups.filter(m => m.round === r));
      sideBRounds.push(sideBMatchups.filter(m => m.round === r));
    }

    const getRoundName = (idx, total) => {
      if (idx === total - 1) return "Conf Final";
      if (idx === total - 2 && total > 2) return "Semis";
      if (idx === 0) return "Round 1";
      return `Round ${idx + 1}`;
    };

    const matchupHeight = 52;
    const baseGap = 8;

    return (
      <div className="overflow-x-auto py-2">
        <div className="flex items-stretch min-w-max">
          <div className="flex items-center">
            <div className="text-center mr-2">
              <div className="text-[10px] font-bold text-blue-400 px-2 py-1 bg-blue-500/10 rounded writing-mode-vertical transform -rotate-180" style={{ writingMode: 'vertical-rl' }}>
                {playoff.conference_a_name || "Conf A"}
              </div>
            </div>
            <div className="flex gap-1">
              {sideARounds.map((round, roundIdx) => {
                const verticalSpacing = Math.pow(2, roundIdx) * (matchupHeight + baseGap);
                const initialOffset = (verticalSpacing - matchupHeight) / 2;
                
                return (
                  <div key={roundIdx} className="flex flex-col min-w-[85px]">
                    <div className="text-[10px] text-zinc-500 text-center mb-1 h-4">{getRoundName(roundIdx, sideARounds.length)}</div>
                    <div className="flex flex-col relative" style={{ gap: `${verticalSpacing - matchupHeight}px`, paddingTop: `${initialOffset}px` }}>
                      {round.map((matchup) => renderMatchup(matchup, "A"))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mx-3">
            <div className="text-[10px] font-bold text-orange-400 text-center mb-1 px-2 py-1 bg-orange-500/10 rounded h-4 flex items-center">
              CHAMPIONSHIP
            </div>
            <div className="flex items-center justify-center" style={{ minHeight: `${Math.pow(2, numRounds - 1) * (matchupHeight + baseGap)}px` }}>
              {championship && (
                <div className="min-w-[100px]">
                  {renderMatchup(championship, "championship")}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className="flex gap-1 flex-row-reverse">
              {sideBRounds.map((round, roundIdx) => {
                const verticalSpacing = Math.pow(2, roundIdx) * (matchupHeight + baseGap);
                const initialOffset = (verticalSpacing - matchupHeight) / 2;
                
                return (
                  <div key={roundIdx} className="flex flex-col min-w-[85px]">
                    <div className="text-[10px] text-zinc-500 text-center mb-1 h-4">{getRoundName(roundIdx, sideBRounds.length)}</div>
                    <div className="flex flex-col relative" style={{ gap: `${verticalSpacing - matchupHeight}px`, paddingTop: `${initialOffset}px` }}>
                      {round.map((matchup) => renderMatchup(matchup, "B"))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center ml-2">
              <div className="text-[10px] font-bold text-red-400 px-2 py-1 bg-red-500/10 rounded" style={{ writingMode: 'vertical-rl' }}>
                {playoff.conference_b_name || "Conf B"}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const rounds = [];
  for (let r = 1; r <= numRounds; r++) {
    rounds.push(playoff.matchups?.filter(m => m.round === r) || []);
  }

  if (rounds.every(r => r.length === 0)) {
    return <p className="text-sm text-zinc-500">Bracket not yet seeded</p>;
  }

  const matchupHeight = 52;
  const baseGap = 8;

  return (
    <div className="flex gap-2 overflow-x-auto py-2">
      {rounds.map((round, roundIdx) => {
        const verticalSpacing = Math.pow(2, roundIdx) * (matchupHeight + baseGap);
        const initialOffset = (verticalSpacing - matchupHeight) / 2;
        
        return (
          <div key={roundIdx} className="flex flex-col min-w-[90px]">
            <div className="text-[10px] text-zinc-500 text-center mb-1">
              {roundIdx === rounds.length - 1 ? "Final" : `Round ${roundIdx + 1}`}
            </div>
            <div className="flex flex-col" style={{ gap: `${verticalSpacing - matchupHeight}px`, paddingTop: `${initialOffset}px` }}>
              {round.map(matchup => renderMatchup(matchup))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
